/**
 * Ластик на чертеже, как в Ремпланнере: объект под курсором подсвечен красным, клик его удаляет.
 *
 * Что под курсором, узнаём по зоне захвата объекта (`planObjectAt`), поэтому ластик работает
 * со всем, что на плане можно взять: стенами, проёмами, мебелью, приборами на стенах,
 * светильниками, кабелями, трубами и комментариями. Подсветка — красный SVG-фильтр
 * (`HoverTints`, `useHoverTint`). Удаление — тем же `remove`, что у клавиши Delete:
 * с зависимыми объектами и записью в историю.
 * Клик по пустому месту выключает ластик (инструмент снова «Выбор»), а потянуть пустое
 * место — это по-прежнему панорама, ластик остаётся.
 */
import { useRef, type PointerEvent } from "react";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";
import { planStageAllows, useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { useWallsStore } from "@/shared/store/layers";
import { planObjectAt } from "../logic";
import { ERASE_TINT_ID } from "../ui/HoverTints";
import { useHoverTint } from "./useHoverTint";

/** Какой вид объекта стоит за зоной захвата. Ручки размера и поворота ластик не трогает. */
const KIND_OF_ROLE: Record<string, SelectionKind> = {
	wall: SelectionKind.Wall,
	opening: SelectionKind.Opening,
	item: SelectionKind.Item,
	fixture: SelectionKind.Fixture,
	cable: SelectionKind.Cable,
	lightingConnection: SelectionKind.LightingConnection,
	pipe: SelectionKind.Pipe,
	comment: SelectionKind.Comment,
};

/** Насколько может сдвинуться курсор, чтобы нажатие ещё считалось кликом, а не панорамой */
const CLICK_SLOP_PX = 4;

interface Erasable {
	kind: SelectionKind;
	id: string;
	/** Что подсвечивать: нарисованный объект, а не его прозрачную зону захвата */
	element: SVGElement;
}

const erasableAt = (target: EventTarget | null): Erasable | null => {
	const hit = planObjectAt(target);
	let kind = hit ? KIND_OF_ROLE[hit.role] : undefined;
	if (!hit || kind === undefined) return null;
	// На этапах объектов («Мебель», «Электрика»…) стены и проёмы готовы — ластик их не трогает
	const { planStage } = useEditorStore.getState();
	if (
		(kind === SelectionKind.Wall && !planStageAllows(planStage, "walls")) ||
		(kind === SelectionKind.Opening && !planStageAllows(planStage, "openings"))
	)
		return null;
	// У светильника на потолке зона захвата тоже «fixture» — он лежит в items слоя электрики
	if (
		kind === SelectionKind.Fixture &&
		hit.id in useProjectStore.getState().project.layers.electrical.items
	)
		kind = SelectionKind.Light;
	return { kind, id: hit.id, element: hit.element };
};

export const useEraseTool = () => {
	const tool = useEditorStore((s) => s.tool);
	// Сменили инструмент — красной подсветки на чертеже остаться не должно
	const tint = useHoverTint(ERASE_TINT_ID, tool === Tool.Erase);
	/** Где нажали на пустое место: отпустили там же — это клик, а не панорама */
	const emptyPress = useRef<{ x: number; y: number } | null>(null);

	const clearHover = () => tint.setHovered(null);

	const handlePointerMove = (e: PointerEvent<Element>) => {
		if (tool !== Tool.Erase) return;
		tint.setHovered(erasableAt(e.target)?.element ?? null);
	};

	/** Клик ластиком: удалил объект — true, событие дальше не идёт. */
	const handlePointerDown = (e: PointerEvent<Element>): boolean => {
		if (tool !== Tool.Erase || e.button !== 0 || e.shiftKey) return false;
		const target = erasableAt(e.target);
		if (!target) {
			emptyPress.current = { x: e.clientX, y: e.clientY };
			return false;
		}
		e.stopPropagation();
		e.preventDefault();
		clearHover();
		useProjectStore.getState().pushHistory();
		const { planStage, selection, setSelection } = useEditorStore.getState();
		if (planStage === "demolition" && target.kind === SelectionKind.Wall) {
			const wall = useWallsStore.getState().items.walls[target.id];
			if (wall) {
				useWallsStore.getState().updateWall(target.id, { demolished: !wall.demolished });
			}
			return true;
		}
		remove({ kind: target.kind, id: target.id });
		if (selection?.id === target.id) setSelection(null);
		return true;
	};

	/** Отпустили кнопку там же, где нажали на пустое место, — выключаем ластик. */
	const handlePointerUp = (e: PointerEvent<Element>) => {
		const press = emptyPress.current;
		emptyPress.current = null;
		if (tool !== Tool.Erase || !press || e.type === "pointercancel") return;
		if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > CLICK_SLOP_PX) return;
		useEditorStore.getState().setTool(Tool.Select);
	};

	return { handlePointerMove, handlePointerDown, handlePointerUp, clearHover };
};
